import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { buscarBaseLegalPorVersao } from "../services/api-piscofins.service";
import { buscarPlanTribBaseLegal } from "../services/api-planejamento-tributario.service";

/**
 * Formata CNPJ para exibição
 */
const formatarCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cnpjLimpo = cnpj.replace(/\D/g, "");
  if (cnpjLimpo.length === 14) {
    return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return cnpj;
};

/**
 * Formata valor monetário para exibição
 */
const formatarMoeda = (valor = 0) => {
  const num = typeof valor === "number" ? valor : parseFloat(valor) || 0;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(num);
};

/**
 * Formata número para exibição brasileira
 */
const formatarNumero = (numero) => {
  if (numero === null || numero === undefined || numero === "") return "0,00";
  const num = typeof numero === "number" ? numero : parseFloat(numero) || 0;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Gera HTML da página 1 - Resumo Geral
 */
const gerarHTMLPagina1 = ({ oportunidades, totais, creditosAdministrativos, creditosJudiciais, infoCabecalho }) => {
  return `
    <div class="page">
      <div class="card">
        <!-- Cabeçalho de Informações -->
        <div class="infoGrid">
          <div class="infoItem">
            <span>Empresa</span>
            <strong>${infoCabecalho.empresaNome || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>CNPJ</span>
            <strong>${formatarCnpj(infoCabecalho.cnpjEmpresa) || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>Período</span>
            <strong>${infoCabecalho.periodoFormatado || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>Temas selecionados</span>
            <strong>${infoCabecalho.temasSelecionados || "Não informado"}</strong>
          </div>
        </div>

        <!-- Cards de Totais -->
        <div class="totalsRow">
          <div class="totalCard totalPis">
            <span>Total PIS</span>
            <strong>${formatarMoeda(totais.totalPis || 0)}</strong>
          </div>
          <div class="totalCard totalCofins">
            <span>Total COFINS</span>
            <strong>${formatarMoeda(totais.totalCofins || 0)}</strong>
          </div>
          <div class="totalCard totalEconomia">
            <span>Economia Total</span>
            <strong>${formatarMoeda(totais.economiaTotal || 0)}</strong>
          </div>
        </div>

        <!-- Distribuição por Tipo de Crédito -->
        <div class="distributionGrid">
          <div class="distributionCard">
            <h3 class="distributionCardTitle">💼 Créditos Administrativos</h3>
            <div class="distributionItem">
              <span class="distributionLabel">PIS</span>
              <span class="distributionValue distributionValuePis">
                ${formatarMoeda(creditosAdministrativos.pis || 0)}
              </span>
            </div>
            <div class="distributionItem">
              <span class="distributionLabel">COFINS</span>
              <span class="distributionValue distributionValueCofins">
                ${formatarMoeda(creditosAdministrativos.cofins || 0)}
              </span>
            </div>
            <div class="distributionItem distributionItemTotal">
              <span class="distributionLabelTotal">Total</span>
              <span class="distributionValueTotal">
                ${formatarMoeda(creditosAdministrativos.total || 0)}
              </span>
            </div>
          </div>

          <div class="distributionCard">
            <h3 class="distributionCardTitle">⚖️ Créditos Judiciais</h3>
            <div class="distributionItem">
              <span class="distributionLabel">PIS</span>
              <span class="distributionValue distributionValuePis">
                ${formatarMoeda(creditosJudiciais.pis || 0)}
              </span>
            </div>
            <div class="distributionItem">
              <span class="distributionLabel">COFINS</span>
              <span class="distributionValue distributionValueCofins">
                ${formatarMoeda(creditosJudiciais.cofins || 0)}
              </span>
            </div>
            <div class="distributionItem distributionItemTotal">
              <span class="distributionLabelTotal">Total</span>
              <span class="distributionValueTotal">
                ${formatarMoeda(creditosJudiciais.total || 0)}
              </span>
            </div>
          </div>
        </div>

        <!-- Tabela de Oportunidades -->
        <table>
          <thead>
            <tr>
              <th>Oportunidade</th>
              <th>Período Apurado</th>
              <th>Fundamento Legal / Tese Judicial</th>
              <th>Tipo</th>
              <th class="text-right">Valor PIS (R$)</th>
              <th class="text-right">Valor COFINS (R$)</th>
              <th class="text-right">Total (R$)</th>
            </tr>
          </thead>
          <tbody>
            ${oportunidades
              .map((oportunidade) => {
                const isDesabilitada = !oportunidade.temValoresSalvos;
                const tipoClass = oportunidade.tipo === "Administrativo" ? "tipo-administrativo" : "tipo-judicial";
                return `
                  <tr class="${isDesabilitada ? "row-disabled" : ""}">
                    <td><strong>${oportunidade.oportunidade || "-"}</strong></td>
                    <td>${isDesabilitada ? "-" : oportunidade.periodoApurado || "-"}</td>
                    <td class="tese-text">${(oportunidade.fundamentoLegal || "-").split("\n").map((linha) => `<div>${linha}</div>`).join("")}</td>
                    <td>
                      <span class="chip ${tipoClass}">${oportunidade.tipo || "-"}</span>
                    </td>
                    <td class="text-right">${formatarMoeda(oportunidade.valorPis || 0)}</td>
                    <td class="text-right">${formatarMoeda(oportunidade.valorCofins || 0)}</td>
                    <td class="text-right"><strong>${formatarMoeda(oportunidade.total || 0)}</strong></td>
                  </tr>
                `;
              })
              .join("")}
            <!-- Linha de Totais -->
            <tr class="row-total">
              <td colspan="4"><strong>TOTAL GERAL</strong></td>
              <td class="text-right"><strong>${formatarMoeda(totais.totalPis || 0)}</strong></td>
              <td class="text-right"><strong>${formatarMoeda(totais.totalCofins || 0)}</strong></td>
              <td class="text-right"><strong>${formatarMoeda(totais.economiaTotal || 0)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
};

/**
 * Gera HTML de uma página de Base Legal
 */
const gerarHTMLPaginaBaseLegal = ({ linhas, basesLegais, infoCabecalho, totais, nomeScript }) => {
  // Buscar descrição da base legal e baseLegalCredito
  const obterDadosBaseLegal = (baseLegalId) => {
    const baseLegal = basesLegais.find((b) => b.id === baseLegalId);
    return {
      descricao: baseLegal?.descricao || `Base Legal #${baseLegalId}`,
      baseLegalCredito: baseLegal?.baseLegalCredito || null,
    };
  };

  return `
    <div class="page">
      <div class="card">
        <!-- Título da Página -->
        <h2 class="pageTitle">${nomeScript || "Base Legal"}</h2>

        <!-- Cabeçalho de Informações -->
        <div class="infoGrid">
          <div class="infoItem">
            <span>Empresa</span>
            <strong>${infoCabecalho.empresaNome || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>CNPJ</span>
            <strong>${formatarCnpj(infoCabecalho.cnpjEmpresa) || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>Período</span>
            <strong>${infoCabecalho.periodoFormatado || "Não informado"}</strong>
          </div>
          <div class="infoItem">
            <span>Temas selecionados</span>
            <strong>${infoCabecalho.temasSelecionados || "Não informado"}</strong>
          </div>
        </div>

        <!-- Cards de Totais -->
        <div class="totalsRow">
          <div class="totalCard totalEcd">
            <span>Total ECD</span>
            <strong>${formatarMoeda(totais.valorTotalEcd || 0)}</strong>
          </div>
          <div class="totalCard totalPis">
            <span>Total PIS (1,65%)</span>
            <strong>${formatarMoeda(totais.pis || 0)}</strong>
          </div>
          <div class="totalCard totalCofins">
            <span>Total COFINS (7,6%)</span>
            <strong>${formatarMoeda(totais.cofins || 0)}</strong>
          </div>
        </div>

        <!-- Tabela -->
        <table>
          <thead>
            <tr>
              <th>Conta Contábil</th>
              <th class="text-right">Valor Total</th>
              <th class="text-right">PIS</th>
              <th class="text-right">COFINS</th>
              <th>Base Legal de Crédito</th>
            </tr>
          </thead>
          <tbody>
            ${linhas
              .map((linha) => {
                const { descricao, baseLegalCredito } = obterDadosBaseLegal(linha.baseLegalId);
                return `
                  <tr>
                    <td>${descricao}</td>
                    <td class="text-right">${formatarNumero(linha.valorTotalEcd)}</td>
                    <td class="text-right">${formatarNumero(linha.pis)}</td>
                    <td class="text-right">${formatarNumero(linha.cofins)}</td>
                    <td>
                      <div class="baseLegalCell">
                        ${baseLegalCredito ? `<div class="baseLegalCredito">${baseLegalCredito}</div>` : "-"}
                      </div>
                    </td>
                  </tr>
                `;
              })
              .join("")}
            <!-- Linha de Totais -->
            <tr class="row-total">
              <td><strong>TOTAL</strong></td>
              <td class="text-right"><strong>${formatarNumero(totais.valorTotalEcd || 0)}</strong></td>
              <td class="text-right"><strong>${formatarNumero(totais.pis || 0)}</strong></td>
              <td class="text-right"><strong>${formatarNumero(totais.cofins || 0)}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
};

/**
 * Exporta dados do Resumo Geral para PDF
 * @param {Object} params - Parâmetros de exportação
 * @param {Array} params.oportunidades - Array de oportunidades
 * @param {Object} params.totais - Totais do resumo
 * @param {Object} params.creditosAdministrativos - Créditos administrativos
 * @param {Object} params.creditosJudiciais - Créditos judiciais
 * @param {Object} params.infoCabecalho - Informações do cabeçalho
 * @param {number} params.versaoId - ID da versão para buscar dados das bases legais
 * @param {Object} params.valoresSalvosPorScript - Valores salvos por script
 * @param {Array} params.tiposPrevia - Tipos de prévia para mapear scripts
 */
export const exportarResumoGeralPDF = async ({
  oportunidades = [],
  totais = {},
  creditosAdministrativos = {},
  creditosJudiciais = {},
  infoCabecalho = {},
  versaoId,
  valoresSalvosPorScript = {},
  tiposPrevia = [],
}) => {
  try {
    if (!Array.isArray(oportunidades) || oportunidades.length === 0) {
      throw new Error("Nenhum dado disponível para exportar");
    }

    if (!versaoId) {
      throw new Error("ID da versão não informado");
    }

    // 1. Gerar HTML da página 1 (Resumo Geral)
    const htmlPagina1 = gerarHTMLPagina1({
      oportunidades,
      totais,
      creditosAdministrativos,
      creditosJudiciais,
      infoCabecalho,
    });

    // 2. Buscar bases legais (usado em todas as páginas de base legal)
    let basesLegais = [];
    try {
      const resultadoBasesLegais = await buscarPlanTribBaseLegal();
      basesLegais = Array.isArray(resultadoBasesLegais?.itens)
        ? resultadoBasesLegais.itens
        : Array.isArray(resultadoBasesLegais)
        ? resultadoBasesLegais
        : [];
    } catch (error) {
      console.warn("⚠️ Erro ao buscar bases legais:", error);
    }

    // 3. Buscar dados de cada script que tem valores salvos
    const paginasBaseLegal = [];
    const scriptsComValores = Object.keys(valoresSalvosPorScript).filter(
      (script) => valoresSalvosPorScript[script] && (valoresSalvosPorScript[script].pis > 0 || valoresSalvosPorScript[script].cofins > 0)
    );

    for (const script of scriptsComValores) {
      try {
        // Buscar dados agrupados por base legal
        const resultado = await buscarBaseLegalPorVersao(versaoId);
        const linhasBaseLegal = Array.isArray(resultado?.linhas) ? resultado.linhas : [];
        const totaisBaseLegal = resultado?.totais && typeof resultado.totais === "object" ? resultado.totais : {};

        if (resultado?.sucesso && linhasBaseLegal.length > 0) {
          // Buscar nome do script nos tipos de prévia
          const tipoPrevia = tiposPrevia.find((t) => t.script === script);
          const nomeScript = tipoPrevia?.descricao || script;

          const htmlPagina = gerarHTMLPaginaBaseLegal({
            linhas: linhasBaseLegal,
            basesLegais,
            infoCabecalho,
            totais: totaisBaseLegal,
            nomeScript,
          });

          paginasBaseLegal.push(htmlPagina);
        }
      } catch (error) {
        console.warn(`⚠️ Erro ao buscar dados do script ${script}:`, error);
        // Continuar com outros scripts mesmo se um falhar
      }
    }

    // 4. Criar HTML completo com todas as páginas
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Arial', sans-serif;
              padding: 0;
              background: #ffffff;
            }
            .page {
              padding: 20px;
              margin-bottom: 40px;
              width: 100%;
              box-sizing: border-box;
              background: #ffffff;
            }
            .pdf-container {
              width: 100%;
              background: #ffffff;
              box-sizing: border-box;
            }
            .card {
              background: #ffffff;
              border-radius: 14px;
              padding: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              width: 100%;
              box-sizing: border-box;
              overflow: hidden;
            }
            .pageTitle {
              font-size: 18px;
              font-weight: 600;
              color: #0f172a;
              margin-bottom: 20px;
              text-align: center;
            }
            .infoGrid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              padding: 15px;
              background: #f8fafc;
              border-radius: 12px;
              border: 1px solid #e1e6ee;
              margin-bottom: 20px;
            }
            .infoItem {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
            .infoItem span {
              font-size: 12px;
              color: #64748b;
            }
            .infoItem strong {
              color: #0f172a;
              font-size: 14px;
              font-weight: 600;
            }
            .totalsRow {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .totalCard {
              border-radius: 12px;
              padding: 15px;
              color: #ffffff;
              display: flex;
              flex-direction: column;
              gap: 8px;
              align-items: center;
              text-align: center;
            }
            .totalCard span {
              font-size: 12px;
              color: rgba(255, 255, 255, 0.9);
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .totalCard strong {
              font-size: 18px;
              color: #ffffff;
              font-weight: 600;
            }
            .totalPis {
              background: #2563eb;
            }
            .totalCofins {
              background: #dc2626;
            }
            .totalEconomia {
              background: #059669;
            }
            .totalEcd {
              background: #1e3a8a;
            }
            .distributionGrid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .distributionCard {
              background: #f8fafc;
              border: 1px solid #e1e6ee;
              border-radius: 12px;
              padding: 15px;
            }
            .distributionCardTitle {
              font-size: 14px;
              font-weight: 600;
              color: #0f172a;
              margin-bottom: 12px;
            }
            .distributionItem {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 8px 0;
              border-bottom: 1px solid #e1e6ee;
            }
            .distributionItem:last-child {
              border-bottom: none;
            }
            .distributionItemTotal {
              margin-top: 8px;
              padding-top: 12px;
              border-top: 2px solid #0f172a;
            }
            .distributionLabel {
              font-size: 12px;
              color: #64748b;
            }
            .distributionLabelTotal {
              font-size: 13px;
              font-weight: 600;
              color: #0f172a;
            }
            .distributionValue {
              font-size: 13px;
              font-weight: 600;
            }
            .distributionValuePis {
              color: #2563eb;
            }
            .distributionValueCofins {
              color: #dc2626;
            }
            .distributionValueTotal {
              font-size: 14px;
              font-weight: 700;
              color: #0f172a;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              border: 1px solid #dfe3eb;
              border-radius: 12px;
              overflow: hidden;
              table-layout: fixed;
            }
            thead {
              background: #f5f7fb;
            }
            th {
              padding: 12px;
              text-align: left;
              font-weight: 600;
              font-size: 12px;
              text-transform: uppercase;
              color: #1f2a44;
              border-bottom: 1px solid #dfe3eb;
            }
            th.text-right {
              text-align: right;
            }
            td {
              padding: 10px 12px;
              font-size: 11px;
              color: #1f2a44;
              border-bottom: 1px solid #e1e6ee;
            }
            td.text-right {
              text-align: right;
            }
            tbody tr:nth-child(even) {
              background-color: #fbfcff;
            }
            tbody tr.row-disabled {
              background-color: #f1f5f9;
              opacity: 0.6;
            }
            tbody tr.row-total {
              background-color: #f5f7fb;
              font-weight: 600;
            }
            .tese-text {
              font-size: 10px;
              line-height: 1.4;
            }
            .chip {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
            }
            .tipo-administrativo {
              background: #dbeafe;
              color: #1e40af;
            }
            .tipo-judicial {
              background: #fce7f3;
              color: #9f1239;
            }
            .baseLegalCell {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            .baseLegalCredito {
              font-size: 10px;
              color: #64748b;
              font-style: italic;
            }
          </style>
        </head>
        <body style="margin: 0; padding: 0; background: #ffffff;">
          <div class="pdf-container">
            ${htmlPagina1}
            ${paginasBaseLegal.join("")}
          </div>
        </body>
      </html>
    `;

    // 5. Criar iframe completamente isolado
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `
      position: fixed;
      top: -10000px;
      left: -10000px;
      width: 210mm;
      height: 297mm;
      border: none;
      opacity: 0;
      pointer-events: none;
      z-index: -99999;
    `;
    
    document.body.appendChild(iframe);

    // Aguardar iframe carregar
    await new Promise((resolve) => {
      iframe.onload = resolve;
      iframe.src = "about:blank";
    });

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Aguardar renderização do conteúdo no iframe
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Verificar se há conteúdo no iframe
    const container = iframeDoc.querySelector(".pdf-container");
    if (!container) {
      console.error("❌ Container PDF não encontrado no HTML");
      document.body.removeChild(iframe);
      throw new Error("Erro ao criar conteúdo do PDF - container não encontrado");
    }

    const pages = container.querySelectorAll(".page");

    // Garantir que o body tenha estilos corretos
    iframeDoc.body.style.cssText = "margin: 0; padding: 0; width: 100%; background: #ffffff;";
    
    // Aguardar mais um pouco para garantir renderização completa
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Usar o container como elemento para o PDF (como no exportarBaseLegalPDF usa .card)
    const element = container;

    if (!element || element.scrollHeight === 0) {
      console.error("❌ Elemento sem altura ou vazio");
      document.body.removeChild(iframe);
      throw new Error("Erro ao criar conteúdo do PDF - elemento vazio");
    }

    // Gerar nome do arquivo
    const dataAtual = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const cnpjLimpo = infoCabecalho.cnpjEmpresa
      ? infoCabecalho.cnpjEmpresa.replace(/\D/g, "")
      : "";
    const nomeArquivo =
      cnpjLimpo && cnpjLimpo.length === 14
        ? `ResumoGeral_${cnpjLimpo}_${dataAtual}.pdf`
        : `ResumoGeral_${dataAtual}.pdf`;

    try {
      // Gerar PDF página por página usando jsPDF diretamente
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      // Capturar cada página separadamente
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        // Garantir que a página está visível e renderizada
        page.style.display = "block";
        page.style.visibility = "visible";
        page.style.opacity = "1";
        
        // Aguardar um pouco para garantir renderização
        await new Promise((resolve) => setTimeout(resolve, 300));
        
        // Capturar a página como canvas
        const canvas = await html2canvas(page, {
          scale: 1.5,
          useCORS: true,
          logging: false,
          windowWidth: page.scrollWidth || 800,
          windowHeight: page.scrollHeight || 1200,
          foreignObjectRendering: false,
          allowTaint: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calcular dimensões para caber na página A4 mantendo proporção
        const pxToMm = 0.264583; // Conversão de pixels para mm
        const imgWidthMm = imgWidth * pxToMm;
        const imgHeightMm = imgHeight * pxToMm;
        
        // Calcular ratio para caber na página
        const ratio = Math.min(contentWidth / imgWidthMm, contentHeight / imgHeightMm);
        const finalWidth = imgWidthMm * ratio;
        const finalHeight = imgHeightMm * ratio;
        
        // Se a altura exceder o conteúdo disponível, ajustar
        let yPosition = margin;
        if (finalHeight > contentHeight) {
          // Se a imagem for muito alta, centralizar verticalmente
          yPosition = margin + (contentHeight - finalHeight) / 2;
        }
        
        // Adicionar nova página se não for a primeira
        if (i > 0) {
          pdf.addPage();
        }
        
        // Adicionar imagem à página
        pdf.addImage(imgData, "JPEG", margin, yPosition, finalWidth, finalHeight);
      }

      // Salvar o PDF
      pdf.save(nomeArquivo);
    } finally {
      // Remover iframe
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }

    return {
      sucesso: true,
      nomeArquivo,
    };
  } catch (error) {
    console.error("Erro ao exportar Resumo Geral para PDF:", error);
    throw new Error(
      error.message || "Erro ao gerar arquivo PDF. Por favor, tente novamente."
    );
  }
};

